// components/AudioRecorderProvider.tsx
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { useReactMediaRecorder } from 'react-media-recorder';

import {
  availableAudioDevicesAtom,
  mediaBlobUrlAtom,
  monitoringStreamAtom,
  pauseRecordingAtom,
  previewAudioStreamAtom,
  recorderStatusAtom,
  selectedAudioDeviceIdAtom,
  startRecordingAtom,
  stopRecordingAtom,
} from '../../atoms';

const AudioRecorderProvider = ({ children }: { children: ReactNode }) => {
  const selectedDeviceId = useAtomValue(selectedAudioDeviceIdAtom);
  const availableDevices = useAtomValue(availableAudioDevicesAtom);

  // Build audio constraints based on selected device
  const audioConstraints = useMemo(() => {
    if (!selectedDeviceId) {
      return true; // Use default device
    }

    // Check if selected device is still available
    const hasDevice = availableDevices.some(
      (device) => device.deviceId === selectedDeviceId,
    );
    if (!hasDevice) {
      return true; // Fall back to default if device no longer exists
    }

    return {
      deviceId: { exact: selectedDeviceId },
    };
  }, [selectedDeviceId, availableDevices]);

  const {
    status,
    mediaBlobUrl,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearBlobUrl,
    previewAudioStream,
  } = useReactMediaRecorder({
    audio: audioConstraints,
    video: false,
    blobPropertyBag: { type: 'audio/webm' },
  });

  const setStatus = useSetAtom(recorderStatusAtom);
  const setBlobUrl = useSetAtom(mediaBlobUrlAtom);
  const setPreviewAudioStream = useSetAtom(previewAudioStreamAtom);
  const setMonitoringStream = useSetAtom(monitoringStreamAtom);
  const handleStartRecording = useSetAtom(startRecordingAtom);
  const handlePauseRecording = useSetAtom(pauseRecordingAtom);
  const handleStopRecording = useSetAtom(stopRecordingAtom);

  const [isCancelPending, setIsCancelPending] = useState(false);

  // Keep atoms in sync
  useEffect(() => {
    setStatus(status);
    if (status === 'recording') {
      handleStartRecording();
    } else if (status === 'paused') {
      handlePauseRecording();
    } else if (status === 'stopped') {
      handleStopRecording();
    }
  }, [
    status,
    setStatus,
    handleStartRecording,
    handlePauseRecording,
    handleStopRecording,
    setPreviewAudioStream,
  ]);

  useEffect(() => {
    if (isCancelPending && mediaBlobUrl) {
      // cancel flow: wait for blob, then clear it
      clearBlobUrl();
      setBlobUrl(undefined);
      setIsCancelPending(false);
    } else {
      // regular flow: update blob atom
      setBlobUrl(mediaBlobUrl);
    }
  }, [mediaBlobUrl, isCancelPending, clearBlobUrl, setBlobUrl]);

  // sync audio preview stream
  useEffect(() => {
    if (previewAudioStream?.active === true) {
      setPreviewAudioStream(previewAudioStream);
    } else {
      setPreviewAudioStream(null);
    }
  }, [previewAudioStream, setPreviewAudioStream]);

  // Expose the control handlers via window
  // Check audioRecorderControls.ts for a friendly API
  useEffect(() => {
    (window as any).__audioRecorder = {
      start: startRecording,
      stop: stopRecording,
      pause: pauseRecording,
      resume: resumeRecording,
      clear: () => {
        setIsCancelPending(true);
        stopRecording();
        if (previewAudioStream) {
          previewAudioStream.getTracks().forEach((track) => {
            // Force stop even if track is already ended
            if (track.readyState !== 'ended') {
              track.stop();
            } else {
              // For ended tracks, try to disable them
              track.enabled = false;
            }
          });
          setPreviewAudioStream(null);
        }
        // Also clear the monitoring stream atom
        setMonitoringStream(null);
      },
    };
  }, [
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearBlobUrl,
    setBlobUrl,
    previewAudioStream,
    setMonitoringStream,
    setPreviewAudioStream,
  ]);

  return <>{children}</>;
};

export default AudioRecorderProvider;
